import { useFormEditor } from '@contexts/FormContext'
import { CreateAnswer } from '@models/Answer'
import { SBAPI } from '@supplement-bacon/react-toolkit/utils'
import {
  CAMPAIGN_EMPLOYEE_MANAGER_URL,
  CAMPAIGN_EMPLOYEE_URL,
} from '@utils/urls'
import {
  Form as ANTDForm,
  App,
  Card,
  Flex,
  Space,
  Steps,
  Typography,
} from 'antd'
import { debounce } from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Answer } from '../answer/Answer'
import PageTitle from '../utils/PageTitle'
import { Form } from './Form'
import StepperNavigation from './StepperNavigation'

const { Title } = Typography

interface Props {}

export const EditorWrapper = ({}: Props) => {
  const navigate = useNavigate()
  const { message } = App.useApp()

  const {
    form,
    campaignEmployee,
    isEmployee,
    formInstance,
    steps,
    activeTab,
    setActiveTab,
    isFulfilled,
  } = useFormEditor()

  if (
    form === undefined ||
    campaignEmployee === undefined ||
    formInstance == undefined
  )
    return <></>

  const [currentStepHasError, setCurrentStepHasError] = useState<boolean>(false)

  const baseUrl = isEmployee
    ? CAMPAIGN_EMPLOYEE_URL
    : CAMPAIGN_EMPLOYEE_MANAGER_URL

  const handleValueChanged = (changedValues: any, values: any) => {
    const data: CreateAnswer[] = Object.keys(values).map((valueId) => ({
      formItem: Number(valueId),
      answer: values[valueId].answer ?? null,
      options: values[valueId].options ?? null,
      managerComment: values[valueId].managerComment ?? null,
    }))

    SBAPI.Post(`${baseUrl}/${campaignEmployee.id}/answers`, {
      answers: data,
    }).catch(() => {
      message.error("Erreur lors de l'enregistrement de votre réponse")
    })
  }

  const debouncedValueChanged = useMemo(
    () => debounce(handleValueChanged, 1000),
    [form]
  )

  const fulfill = () =>
    SBAPI.Post(`${baseUrl}/${campaignEmployee.id}/fulfill`)
      .then(() => true)
      .catch(() => {
        message.error("Erreur lors de l'enregistrement de votre formulaire")
      })

  useEffect(() => {
    // Hydrate form instance with campaignEmployee data
    campaignEmployee.answers.forEach((answerData) => {
      const options = isEmployee
        ? answerData.employeeOptionIds
        : answerData.managerOptionIds
      const answer = isEmployee
        ? answerData.employeeAnswer
        : answerData.managerAnswer

      formInstance.setFieldsValue({
        [answerData.formItemId]: {
          answer: answer,
          options: options,
          managerComment: answerData.managerComment,
        },
      })
    })
  }, [campaignEmployee])

  useEffect(() => {
    return () => {
      debouncedValueChanged.cancel()
    }
  })

  return (
    <Flex gap={16} vertical>
      <PageTitle
        level={3}
        title={
          <Space align="end">
            <Title style={{ margin: 0 }} level={1}>
              {form.name}
            </Title>
          </Space>
        }
      />
      <Steps
        size="small"
        status={currentStepHasError ? 'error' : undefined}
        current={activeTab}
        items={steps}
        onChange={(index) => setActiveTab(index)}
      />
      <Card>
        <ANTDForm
          scrollToFirstError
          layout="vertical"
          className="w-full"
          form={formInstance}
          onValuesChange={debouncedValueChanged}
        >
          <Flex vertical gap={16}>
            {steps[activeTab].page.items.map((item) => (
              <Answer key={item.id} item={item}>
                <Form.FormItem item={item} />
              </Answer>
            ))}
          </Flex>
        </ANTDForm>
      </Card>
      <StepperNavigation
        onPrevTapped={() => {
          setCurrentStepHasError(false)
        }}
        onNextTapped={(validated) => {
          if (!isFulfilled) {
            handleValueChanged(undefined, formInstance.getFieldsValue())
          }
          setCurrentStepHasError(!validated)
        }}
        onDoneTapped={(validated) => {
          setCurrentStepHasError(!validated)
          if (validated) {
            fulfill().then((success) => {
              if (!success) return
              navigate('/')
            })
          }
        }}
      />
    </Flex>
  )
}

export type EditorWrapperType = { Wrapper: typeof EditorWrapper }
